import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import axios from "axios";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import { startFundsAutoUpdate } from "redux/app/actions/funds.action";
import SrCurrency from "components/common/SrCurrency";
import {
  SrAlert,
  SrCurrencyInput,
  SrButton,
  SrInput,
  SrDialog,
} from "../../../common";
import { TRANSFER } from "../../../../constants/api";
import { validations } from "utils";

const WithdrawTypes = {
  TOTAL: "TOTAL",
  PARTIAL: "PARTIAL",
};

const BankAccountWithdrawDialog = ({ bankAccounts, handleClose, open }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const funds = useSelector(({ app }) => app.funds);
  const [initialValues, setInitialValues] = useState({
    type: WithdrawTypes.TOTAL,
    customAmount: "",
    bankAccount: "",
  });
  const [error, setError] = useState();

  useEffect(() => {
    dispatch(startFundsAutoUpdate(true));
  }, [dispatch]);

  useEffect(() => {
    setInitialValues({
      type: WithdrawTypes.TOTAL,
      customAmount: "",
      bankAccount: bankAccounts.find((acc) => acc.default)?.id || "",
    });
  }, [bankAccounts]);

  const onSubmit = async (values) => {
    try {
      const params = {
        amount:
          values.type === WithdrawTypes.PARTIAL
            ? values.customAmount
            : funds.available_funds,
        bank_account: values.bankAccount,
        description: `RetManual $${values.customAmount}`,
      };

      const result = await axios.post(TRANSFER, params);

      if (result?.success) {
        dispatch(startFundsAutoUpdate(true));
        toast.success(
          intl.formatMessage(
            { id: "bankAccounts.withdrawForm.success" },
            {
              amount: `$${params.amount}`,
            }
          )
        );
        setError();
        handleClose();
      }
    } catch (error) {
      setError(
        error?.response?.data?.error?.message ||
          intl.formatMessage({ id: "errors.somethingWrong" })
      );
    }
  };

  const validationSchema = Yup.object().shape({
    bankAccount: Yup.string().required(
      intl.formatMessage({ id: "common.requiredField" })
    ),
    customAmount: Yup.number().when("type", {
      is: (value) => value === WithdrawTypes.PARTIAL,
      then: validations.amount
        .required(intl.formatMessage({ id: "common.requiredField" }))
        .max(
          funds.available_funds,
          intl.formatMessage(
            { id: "bankAccounts.withdrawForm.amount.max" },
            { maxAmount: funds.available_funds }
          )
        ),
    }),
  });

  return (
    <SrDialog
      handleClose={handleClose}
      open={open}
      heading={intl.formatMessage({ id: "bankAccounts.withdrawForm.title" })}
    >
      <Formik
        initialValues={initialValues}
        validateOnChange="true"
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ errors, touched, handleChange, values, isSubmitting }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SrInput
                  error={touched.bankAccount && Boolean(errors.bankAccount)}
                  fullWidth
                  helperText={touched.bankAccount && errors.bankAccount}
                  id="bankAccount"
                  label={intl.formatMessage({
                    id: "bankAccounts.withdrawForm.destinationAccount",
                  })}
                  onChange={handleChange("bankAccount")}
                  select
                  value={values.bankAccount}
                  variant="outlined"
                >
                  {bankAccounts.map((bankAccount) => (
                    <MenuItem key={bankAccount.id} value={bankAccount.id}>
                      {`${bankAccount.alias} (${bankAccount.bank_name} / ${bankAccount.account_number_suffix})`}
                    </MenuItem>
                  ))}
                </SrInput>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <RadioGroup
                    name="type"
                    value={values.type}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value={WithdrawTypes.TOTAL}
                      control={<Radio />}
                      label={<SrCurrency value={funds.available_funds} />}
                    />
                    <FormControlLabel
                      value={WithdrawTypes.PARTIAL}
                      control={<Radio />}
                      label={intl.formatMessage({
                        id: "bankAccounts.withdrawForm.otherAmount",
                      })}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {values.type === WithdrawTypes.PARTIAL && (
                <Grid item xs={12}>
                  <SrCurrencyInput
                    label={intl.formatMessage({
                      id: "bankAccounts.withdrawForm.customAmount",
                    })}
                    id="customAmount"
                    variant="outlined"
                    fullWidth
                    value={values.customAmount}
                    onChange={handleChange}
                    error={touched.customAmount && Boolean(errors.customAmount)}
                    helperText={touched.customAmount && errors.customAmount}
                  />
                </Grid>
              )}
              {error && (
                <Grid item xs={12}>
                  <SrAlert severity="error">{error}</SrAlert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box textAlign="right">
                  <SrButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    {intl.formatMessage({
                      id: "bankAccounts.withdrawForm.confirm",
                    })}
                  </SrButton>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </SrDialog>
  );
};

BankAccountWithdrawDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  bankAccounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default BankAccountWithdrawDialog;
